/**
 * PhotoViewerModal
 * Fullscreen image viewer with pan and zoom support, and previous/next
 * (buttons and arrow keys) when the caller passes `onPrev` / `onNext`.
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, X, ZoomIn, ZoomOut } from 'lucide-react';
import './PhotoViewerModal.css';

interface PhotoViewerModalProps {
    show: boolean;
    imageUrl: string;
    /** While true, show a loading bar instead of the image (full-size fetch in flight). */
    loading?: boolean;
    onClose: () => void;
    /** Previous photo in the group; omitted = no button */
    onPrev?: () => void;
    /** Next photo in the group; omitted = no button */
    onNext?: () => void;
    /** e.g. "2 / 5", shown in the toolbar */
    counter?: string;
}

const MIN_SCALE = 1;
const MAX_SCALE = 4;
const ZOOM_STEP = 0.5;

const PhotoViewerModal: React.FC<PhotoViewerModalProps> = ({
    show,
    imageUrl,
    loading = false,
    onClose,
    onPrev,
    onNext,
    counter,
}) => {
    const [scale, setScale] = useState(1);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [initialPinchDistance, setInitialPinchDistance] = useState<number | null>(null);
    const [initialScale, setInitialScale] = useState(1);

    const containerRef = useRef<HTMLDivElement>(null);
    const imageRef = useRef<HTMLImageElement>(null);

    // Reset zoom when the viewer opens and when it moves to another photo
    useEffect(() => {
        if (show) {
            setScale(1);
            setPosition({ x: 0, y: 0 });
        }
    }, [show, imageUrl]);

    // Handle escape key
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!show) return;
            if (e.key === 'Escape') onClose();
            else if (e.key === 'ArrowLeft' && onPrev) onPrev();
            else if (e.key === 'ArrowRight' && onNext) onNext();
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [show, onClose, onPrev, onNext]);

    // Prevent body scroll when modal is open
    useEffect(() => {
        if (show) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [show]);

    const handleZoomIn = useCallback(() => {
        setScale((prev) => Math.min(prev + ZOOM_STEP, MAX_SCALE));
    }, []);

    const handleZoomOut = useCallback(() => {
        setScale((prev) => {
            const newScale = Math.max(prev - ZOOM_STEP, MIN_SCALE);
            if (newScale === MIN_SCALE) {
                setPosition({ x: 0, y: 0 });
            }
            return newScale;
        });
    }, []);

    const handleWheel = useCallback((e: React.WheelEvent) => {
        e.preventDefault();
        const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
        setScale((prev) => {
            const newScale = Math.max(MIN_SCALE, Math.min(prev + delta, MAX_SCALE));
            if (newScale === MIN_SCALE) {
                setPosition({ x: 0, y: 0 });
            }
            return newScale;
        });
    }, []);

    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        if (scale > 1) {
            e.preventDefault();
            setIsDragging(true);
            setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
        }
    }, [scale, position]);

    const handleMouseMove = useCallback((e: React.MouseEvent) => {
        if (isDragging && scale > 1) {
            setPosition({
                x: e.clientX - dragStart.x,
                y: e.clientY - dragStart.y,
            });
        }
    }, [isDragging, scale, dragStart]);

    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
    }, []);

    // Touch handlers for mobile pinch-zoom
    const getTouchDistance = (touches: React.TouchList): number => {
        if (touches.length < 2) return 0;
        const dx = touches[0].clientX - touches[1].clientX;
        const dy = touches[0].clientY - touches[1].clientY;
        return Math.sqrt(dx * dx + dy * dy);
    };

    const getTouchCenter = (touches: React.TouchList): { x: number; y: number } => {
        if (touches.length < 2) {
            return { x: touches[0].clientX, y: touches[0].clientY };
        }
        return {
            x: (touches[0].clientX + touches[1].clientX) / 2,
            y: (touches[0].clientY + touches[1].clientY) / 2,
        };
    };

    const handleTouchStart = useCallback((e: React.TouchEvent) => {
        if (e.touches.length === 2) {
            e.preventDefault();
            setInitialPinchDistance(getTouchDistance(e.touches));
            setInitialScale(scale);
        } else if (e.touches.length === 1 && scale > 1) {
            setIsDragging(true);
            setDragStart({
                x: e.touches[0].clientX - position.x,
                y: e.touches[0].clientY - position.y,
            });
        }
    }, [scale, position]);

    const handleTouchMove = useCallback((e: React.TouchEvent) => {
        if (e.touches.length === 2 && initialPinchDistance) {
            e.preventDefault();
            const currentDistance = getTouchDistance(e.touches);
            const scaleChange = currentDistance / initialPinchDistance;
            const newScale = Math.max(MIN_SCALE, Math.min(initialScale * scaleChange, MAX_SCALE));
            setScale(newScale);

            if (newScale === MIN_SCALE) {
                setPosition({ x: 0, y: 0 });
            }
        } else if (e.touches.length === 1 && isDragging && scale > 1) {
            setPosition({
                x: e.touches[0].clientX - dragStart.x,
                y: e.touches[0].clientY - dragStart.y,
            });
        }
    }, [initialPinchDistance, initialScale, isDragging, scale, dragStart]);

    const handleTouchEnd = useCallback(() => {
        setIsDragging(false);
        setInitialPinchDistance(null);
    }, []);

    const handleBackdropClick = useCallback((e: React.MouseEvent) => {
        if (e.target === containerRef.current) {
            onClose();
        }
    }, [onClose]);

    const handleDoubleClick = useCallback(() => {
        if (scale > 1) {
            setScale(1);
            setPosition({ x: 0, y: 0 });
        } else {
            setScale(2);
        }
    }, [scale]);

    if (!show) return null;

    // Portaled to <body>, like Bootstrap's modals, so it sits above an open
    // modal whatever stacking context the caller renders in.
    return createPortal(
        <div
            ref={containerRef}
            className="photo-viewer-modal"
            onClick={handleBackdropClick}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
        >
            <div className="photo-viewer-toolbar">
                <button
                    className="photo-viewer-btn"
                    onClick={handleZoomOut}
                    disabled={loading || scale <= MIN_SCALE}
                >
                    <ZoomOut size={24} />
                </button>
                <span className="photo-viewer-scale">{Math.round(scale * 100)}%</span>
                <button
                    className="photo-viewer-btn"
                    onClick={handleZoomIn}
                    disabled={loading || scale >= MAX_SCALE}
                >
                    <ZoomIn size={24} />
                </button>
                {counter && <span className="photo-viewer-counter">{counter}</span>}
                <button className="photo-viewer-btn photo-viewer-close" onClick={onClose} aria-label="Stäng">
                    <X size={24} />
                </button>
            </div>

            {onPrev && (
                <button className="photo-viewer-btn photo-viewer-nav photo-viewer-nav--prev" onClick={onPrev} aria-label="Föregående bild">
                    <ChevronLeft size={28} />
                </button>
            )}
            {onNext && (
                <button className="photo-viewer-btn photo-viewer-nav photo-viewer-nav--next" onClick={onNext} aria-label="Nästa bild">
                    <ChevronRight size={28} />
                </button>
            )}

            <div
                className="photo-viewer-image-container"
                onWheel={handleWheel}
                onMouseDown={handleMouseDown}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onDoubleClick={handleDoubleClick}
            >
                {loading ? (
                    <div className="photo-viewer-loading" role="status" aria-live="polite">
                        <div className="photo-viewer-loading-bar">
                            <div className="photo-viewer-loading-bar-fill" />
                        </div>
                        <span className="photo-viewer-loading-text">Laddar bild…</span>
                    </div>
                ) : (
                    <img
                        ref={imageRef}
                        src={imageUrl}
                        alt=""
                        className="photo-viewer-image"
                        style={{
                            transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
                            cursor: scale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'zoom-in',
                        }}
                        draggable={false}
                    />
                )}
            </div>
        </div>,
        document.body
    );
};

export default PhotoViewerModal;
